import { YearToDateStrategy } from '~/composables/date-range-strategies'
import { getCompEndMonth } from '~/composables/financials-grid-commons'
import { NumberFormatEnum } from '~/protos/gen/stcs_user.entity'
import { urlParamPartObj } from '~/types/route.entity'
import type { FpnaScopeParams } from '~/types/stcs_wapp_ctx_data.entity'

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export function get(obj: any, key?: string): any {
  if (key) {
    return key.split('.').reduce((o, x) => {
      return typeof o === 'undefined' || o === null ? o : o[x]
    }, obj)
  }
  throw new Error(`key is undefined for obj: '${obj}'`)
}

// Helper function to update URL params
export function updateFpnaUrlParams(scope_params: FpnaScopeParams) {
  const currentUrl = window.location.href.split('?')[0] // Get the base URL (excluding query)

  // Ensure all keys are present, defaulting to empty string
  const newQueryParams: URLSearchParams = new URLSearchParams({
    actuals: scope_params.actuals_date_str ?? '',
    plan: scope_params.plan_date_str ?? '',
    range: scope_params.range_date_str ?? '',
  })

  // Construct the new URL
  const updatedUrl = `${currentUrl}?${newQueryParams.toString()}`

  // Update the URL without reloading the page
  window.history.pushState({}, '', updatedUrl)
}

export function verifyAndGetActualsUrlParam(actualsStr: string | undefined, minimumDateBoundary: Date, maximumDateBoundary: Date): { month: number; year: number } {
  if (!actualsStr) {
    throw new Error('actuals is undefined')
  }

  const actuals = parseAndCorrectDate(actualsStr, minimumDateBoundary, maximumDateBoundary)
  return { month: actuals.getMonth(), year: actuals.getFullYear() }
}

export function verifyAndGetRangeUrlParam(range: string | undefined, minimumDateBoundary: Date, maximumDateBoundary: Date): { month: number; year: number }[] {
  if (!range || range.split(',').length !== 2) {
    const ytdStrategy = new YearToDateStrategy(maximumDateBoundary, getCompEndMonth() || 12)
    return [
      { month: ytdStrategy.dateRange.fromDate.getMonth(), year: ytdStrategy.dateRange.fromDate.getFullYear() },
      { month: ytdStrategy.dateRange.toDate.getMonth(), year: ytdStrategy.dateRange.toDate.getFullYear() },
    ]
  }
  const rangeArr = range.split(',')
  const startDate = parseAndCorrectDate(rangeArr[0], minimumDateBoundary, maximumDateBoundary)
  const endDate = parseAndCorrectDate(rangeArr[1], minimumDateBoundary, maximumDateBoundary)

  if (startDate.getTime() > endDate.getTime()) {
    return [
      { month: minimumDateBoundary.getMonth(), year: minimumDateBoundary.getFullYear() },
      { month: maximumDateBoundary.getMonth(), year: maximumDateBoundary.getFullYear() },
    ]
  }

  return [
    { month: startDate.getMonth(), year: startDate.getFullYear() },
    { month: endDate.getMonth(), year: endDate.getFullYear() },
  ]
}

export function getRangeUrlParamStr(range: { month: number; year: number }[]): string {
  const rangeParamStr = `${getDateStr(range[0])},${getDateStr(range[1])}`
  urlParamPartObj.range.key_value = rangeParamStr
  return rangeParamStr
}

export function getActualsUrlParamStr(actuals: { month: number; year: number }): string {
  const actualsParamStr = getDateStr(actuals)
  urlParamPartObj.actuals.key_value = actualsParamStr
  return actualsParamStr
}

export function getDateStr(date: { month: number; year: number }): string {
  const month = (date.month + 1).toString().padStart(2, '0')
  return `${date.year}-${month}`
}

function parseAndCorrectDate(dateStr: string, minDate: Date, maxDate: Date): Date {
  const [year, month] = dateStr.split('-').map(Number)
  let date = new Date(year, month - 1, 1)

  if (date.getTime() < minDate.getTime()) {
    date = minDate
  } else if (date.getTime() > maxDate.getTime()) {
    date = maxDate
  }
  return date
}

// RegEx patterns for slugify function
const SPACES_REGEX = /\s+/g
const NON_WORD_REGEX = /[^\w\-]+/g
const MULTIPLE_HYPHENS_REGEX = /\-\-+/g
const START_HYPHENS_REGEX = /^-+/
const END_HYPHENS_REGEX = /-+$/

export function slugify(text: string) {
  return text.toString().toLowerCase().replace(SPACES_REGEX, '_').replace(NON_WORD_REGEX, '').replace(MULTIPLE_HYPHENS_REGEX, '_').replace(START_HYPHENS_REGEX, '').replace(END_HYPHENS_REGEX, '')
}

export function getScaleFactor(format: NumberFormatEnum): number {
  switch (format) {
    case NumberFormatEnum.NFO_TAUSEND:
      return 1_000
    case NumberFormatEnum.NFO_MILLION:
      return 1_000_000
    default:
      return 1
  }
}

export function parseYYYYMM(yyyyMmStr: string): Date {
  const [year, month] = yyyyMmStr.split('-').map(Number)
  // JS months are 0-based => month - 1
  return new Date(year, month - 1, 1)
}

export function hideAllTooltips(): void {
  // Remove all tooltip elements from the DOM
  const tooltips = document.querySelectorAll('.vue-tooltip') || document.querySelectorAll('.data-tooltip')
  tooltips.forEach((tooltip) => {
    if (tooltip instanceof HTMLElement) {
      tooltip.style.opacity = '0'
      setTimeout(() => {
        if (tooltip.parentNode) {
          tooltip.parentNode.removeChild(tooltip)
        }
      }, 150)
    }
  })
}
